<template>
  <div>
    <div v-if="mode === 'upload'">

      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/jpg"
        style="display:none"
        :disabled="disabled"
        @change="handleFileInput"
      />

      <a href="#" @click.prevent="selectFile">
        <div
          :class="!disabled ? 'drop_zone' : 'drop-zone-disable'"
          @drop="dropHandler"
          @dragover.prevent="dragOverHandler"
        >
          <v-icon size="30" :class="disabled && 'icon'">upload_file</v-icon>

          <div class="drop-zone-texts">
            <span class="drop-zone-title">{{ nome }}</span>
            <small class="file-desc">(JPG/JPEG)</small>
            <span class="file-name">{{ fileName }}</span>
          </div>
        </div>
      </a>

      <div v-if="photoPreview" class="mt-3 d-flex flex-column align-center">
        <img :src="photoPreview" class="photo-preview" />
      </div>

    </div>

    <div v-if="mode === 'camera'">

      <input
        v-if="isMobile"
        ref="mobileCamera"
        type="file"
        accept="image/jpeg,image/jpg"
        capture="user"
        style="display:none"
        :disabled="disabled"
        @change="handleFileInput"
      />

      <div v-if="!disabled" class="mt-2 d-flex justify-center">

        <!-- Mobile camera -->
        <v-btn
          v-if="isMobile"
          small color="primary"
          @click="$refs.mobileCamera.click()"
        >
          Tirar Selfie Agora
        </v-btn>

        <!-- Desktop camera -->
        <v-btn
          v-if="!isMobile"
          small color="primary"
          @click="openCamera"
        >
          Tirar Selfie Agora
        </v-btn>

      </div>

      <!-- MODAL DA CÂMERA -->
      <div v-if="cameraActive" class="camera-modal">
        <video ref="video" autoplay width="320"></video>

        <div class="mt-2 d-flex justify-center">
          <v-btn class="mr-2" color="primary" small @click="capturePhoto">
            Capturar
          </v-btn>
          <v-btn color="error" small @click="closeCamera">
            Cancelar
          </v-btn>
        </div>
      </div>

      <div v-if="photoPreview" class="mt-3 d-flex flex-column align-center">
        <img :src="photoPreview" class="photo-preview" />
      </div>

    </div>

    <p v-if="fileErro" class="erro">Por favor selecione {{ nome }}</p>
    <p v-if="fileTypeErro" class="erro">Arquivo inválido. Enviar apenas JPG/JPEG*</p>
    <p v-if="errorSize" class="erro">Arquivo inválido. Enviar documento com até 10MB*</p>

  </div>
</template>

<script>
import GlobalStore from "@/store/modules/global";

export default {
  name: "UploadBiometria",

  props: {
    nome: null,
    fileErro: Boolean,
    fileTypeErro: Boolean,
    fileName: null,
    disabled: Boolean,
    isFoto: Boolean,
    mode: { type: String, required: true } 
  },

  data() {
    return {
      errorSize: false,
      cameraActive: false,
      photoPreview: null,
      stream: null,
      isMobile: false
    };
  },

  mounted() {
    this.isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  },

  methods: {
    dragOverHandler(e) { e.preventDefault(); },

    dropHandler(e) {
      e.preventDefault();
      if (!this.disabled && e.dataTransfer.files.length === 1) {
        const file = e.dataTransfer.files[0];
        this.processImage(file);
      }
    },

    selectFile() {
      if (!this.disabled) this.$refs.fileInput.click();
    },

    handleFileInput(e) {
      if (e.target.files[0]) this.processImage(e.target.files[0]);
    },

    async processImage(file) {
      this.errorSize = false;

      if (file.size > 10485760) {
        this.errorSize = true;
        this.photoPreview = null;
        this.$emit("file", null);
        return;
      }

      if (!file.type.includes("jpeg") && !file.type.includes("jpg")) {
        this.fileTypeErro = true;
        this.photoPreview = null;
        this.$emit("file", null);
        return;
      }

      const processed = this.isFoto ? await this.cropTo3x4(file) : file;

      this.photoPreview = URL.createObjectURL(processed);
      this.$emit("file", processed);
    },

    async cropTo3x4(file) {
      return new Promise(resolve => {
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = 600;
          canvas.height = 800;

          const ctx = canvas.getContext("2d");

          let w = img.width;
          let h = img.height;
          const aspect = 3 / 4;

          if (w / h > aspect) w = h * aspect;
          else h = w / aspect;

          const sx = (img.width - w) / 2;
          const sy = (img.height - h) / 2;

          ctx.drawImage(img, sx, sy, w, h, 0, 0, 600, 800);

          canvas.toBlob(blob => {
            resolve(new File([blob], "biometria.jpg", { type: "image/jpeg" }));
          }, "image/jpeg", 0.95);
        };

        img.src = URL.createObjectURL(file);
      });
    },

    openCamera() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        GlobalStore.setSnackBar({
          show: true,
          message: "Seu navegador não permite acesso à câmera.",
          type: "warning",
          timeout: 4000,
        });
        return;
      }
    
      navigator.mediaDevices
        .getUserMedia({ video: { facingMode: "user" } })
        .then(stream => {
          this.stream = stream;
          this.cameraActive = true;
        
          this.$nextTick(() => {
            this.$refs.video.srcObject = stream;
            this.$refs.video.play();
          });
        })
        .catch(() => {
          GlobalStore.setSnackBar({
            show: true,
            message: "Não foi possível acessar a câmera.",
            type: "warning",
            timeout: 4000,
          });
        });
    },

    capturePhoto() {
      const canvas = document.createElement("canvas");
      canvas.width = this.$refs.video.videoWidth;
      canvas.height = this.$refs.video.videoHeight;

      canvas.getContext("2d").drawImage(this.$refs.video, 0, 0);

      canvas.toBlob(async blob => {
        const file = new File([blob], "biometria.jpg", { type: "image/jpeg" });
        const file3x4 = await this.cropTo3x4(file);
        this.processImage(file3x4);
      });

      this.closeCamera();
    },

    closeCamera() {
      this.cameraActive = false;
      if (this.stream) {
        this.stream.getTracks().forEach(t => t.stop());
        this.stream = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.drop_zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #444a4c;
  border-radius: 4px;
  width: 99%;
  height: 100px;
  padding: 8px 4px;
  margin-left: 1.5px;
  text-align: center;
  background-color: #edebeb;
  margin-top: -5px;
  margin-bottom: 12px;

  .drop-zone-texts {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .drop-zone-title {
    font-weight: 500;
  }

  .file-desc {
    font-size: 11px;
    color: #555;
  }

  .file-name {
    max-width: 95%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: #444;
  }
}

.drop-zone-disable {
  border: 1px dashed #969999;
  border-radius: 4px;
  width: 99%;
  height: 100px;
  padding: 8px 4px;
  margin-left: 1.5px;
  text-align: center;
  background-color: #edebeb;
  margin-top: -5px;
  margin-bottom: 12px;
}

.erro {
  color: #f61a1a;
  font-size: 11.5px;
  margin-top: 4px;
  margin-bottom: 0;
  padding-left: 8px;
}

.icon {
  color: #828484;
}

.camera-modal {
  position: fixed;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  background: #f5f5f5;
  padding: 15px;
  border-radius: 8px;
  z-index: 999;
}

.photo-preview {
  width: 150px;
  height: 200px;
  border: 2px solid #444a4c;
  border-radius: 4px;
  object-fit: cover;
  background: #fff;
}
</style>
